import type { Combo, ComboLineInput, ComboUpsert, Item } from '@erp/shared';
import { Package, ScanLine, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Card, Skeleton } from '../../components/shadcn/index.js';
import {
  Button, Combobox, type ComboboxOption, confirmDialog, NumberInput,
} from '../../components/ui/index.js';
import { api } from '../../lib/api.js';
import { formatMoney, trimMoney } from '../../lib/money.js';
import { normalizeNumericInput, safeDecimal } from '../../lib/numerals.js';

/** One component row — display fields snapshotted at add time. */
export interface ComboDraftRow {
  _key: string;
  componentItemId: string;
  code: string;
  name: string;
  uom: string;
  /** How many of this component are in ONE combo, in base units. */
  quantity: string;
  unitSalePrice: string | null;
  saleCurrencyCode: string | null;
  onHand: string;
}

let keySeq = 0;
const nextKey = (): string => `cl-${++keySeq}`;
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 30;

interface Props {
  /**
   * The BUNDLE item this component list belongs to — null while the product is
   * still being created, since components need an item id to hang off. The box
   * is still filled in normally; the CREATE form saves it the moment the
   * product exists, the way it already does for opening stock and prices.
   */
  itemId: string | null;
  /** Live from the form, so the saving updates as the owner types the price. */
  salePrice: string;
  saleCurrencyCode: string;
  canWrite: boolean;
  /**
   * While creating, the rows live in the FORM, not in here: switching to the
   * Basics tab unmounts this panel, and state kept locally would be silently
   * thrown away. In edit mode they are re-read from the server on mount, so
   * there is nothing to lose and these stay unused.
   */
  draft?: ComboDraftRow[];
  onDraftChange?: (rows: ComboDraftRow[]) => void;
}

/** The rows as the API wants them — shared with the create form's save step. */
export function comboRowsToPayload(rows: ComboDraftRow[]): ComboLineInput[] {
  return rows
    .filter((l) => safeDecimal(l.quantity).gt(0))
    .map((l) => ({
      componentItemId: l.componentItemId,
      unitFactor: '1',
      quantity: safeDecimal(l.quantity).toDecimalPlaces(4).toString(),
    }));
}

/**
 * The item's Combo tab — what is inside the bundle.
 *
 * Selling the bundle takes these components off the shelf; the bundle itself
 * keeps no stock, which is why the grid shows each component's on-hand and how
 * many whole bundles they can still cover. The savings line compares the parts'
 * own prices with the bundle price the owner typed, because "what am I giving
 * away here?" is the question a bundle is actually built to answer.
 */
export function ItemComboTab({ itemId, salePrice, saleCurrencyCode, canWrite, draft, onDraftChange }: Props): JSX.Element {
  const { t, i18n } = useTranslation('combos');
  const [loaded, setLoaded] = useState(false);
  const [exists, setExists] = useState(false);
  const [ownLines, setOwnLines] = useState<ComboDraftRow[]>([]);
  // Edit mode owns its rows (the server is the source); create mode borrows the
  // form's, so they outlive this panel being unmounted.
  const lines = itemId ? ownLines : (draft ?? []);
  const setLines = (next: ComboDraftRow[] | ((prev: ComboDraftRow[]) => ComboDraftRow[])): void => {
    const resolve = (prev: ComboDraftRow[]): ComboDraftRow[] => (typeof next === 'function' ? next(prev) : next);
    if (itemId) setOwnLines(resolve);
    else onDraftChange?.(resolve(draft ?? []));
  };
  const [saving, setSaving] = useState(false);
  const [term, setTerm] = useState('');
  const [search, setSearch] = useState<{ q: string; items: Item[] }>({ q: '', items: [] });
  const itemsById = useRef(new Map<string, Item>());
  const scanWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Nothing on the server yet — the blank editor IS the state.
    if (!itemId) { setLoaded(true); return undefined; }
    let on = true;
    void api().get<Combo>(`/combos/by-item/${itemId}`)
      .then((r) => {
        if (!on) return;
        setExists(true);
        setOwnLines(r.data.lines.map((l) => ({
          _key: nextKey(),
          componentItemId: l.componentItemId,
          code: l.componentCode,
          name: l.componentName,
          uom: l.componentUom,
          // Stored in the line's unit ×factor — shown in base units.
          quantity: trimMoney(safeDecimal(l.quantity).times(safeDecimal(l.unitFactor)).toString()),
          unitSalePrice: l.unitSalePrice,
          saleCurrencyCode: l.saleCurrencyCode,
          onHand: l.onHand,
        })));
      })
      .catch(() => { /* 404 = not a bundle yet — the blank editor IS the empty state */ })
      .finally(() => { if (on) setLoaded(true); });
    return () => { on = false; };
  }, [itemId]);

  // Debounced server item search (transfer-form pattern).
  useEffect(() => {
    const q = term.trim();
    if (!q) { setSearch({ q: '', items: [] }); return undefined; }
    let on = true;
    const handle = window.setTimeout(() => {
      // `stock: 1` so a row added right now shows its real on-hand (and feeds
      // the "can sell right now" figure) instead of sitting at zero until save.
      void api().get<{ items: Item[] }>('/items', { params: { q, limit: SEARCH_LIMIT, stock: 1 } })
        .then((r) => {
          if (!on) return;
          for (const it of r.data.items) itemsById.current.set(it.id, it);
          // A bundle cannot contain itself, and cannot contain another bundle
          // (the server explodes one level, so a nested box would ship empty).
          setSearch({ q, items: r.data.items.filter((it) => it.isActive && it.id !== itemId && !it.isCombo) });
        })
        .catch(() => { if (on) setSearch({ q, items: [] }); });
    }, SEARCH_DEBOUNCE_MS);
    return () => { on = false; window.clearTimeout(handle); };
  }, [term, itemId]);

  const scanOptions = useMemo<ComboboxOption[]>(
    () => search.items.map((it) => ({
      value: it.id,
      label: `${it.code} — ${it.name}`,
      sublabel: it.barcode ?? undefined,
      keywords: `${it.code} ${it.name} ${it.barcode ?? ''} ${search.q}`,
    })),
    [search],
  );

  const focusScan = (): void => { scanWrapRef.current?.querySelector('input')?.focus(); };

  const addItem = (item: Item, addBase: string): void => {
    if (item.id === itemId) {
      toast.error(t('selfReference', { defaultValue: 'A bundle cannot contain itself.' }));
      return;
    }
    if (item.isCombo) {
      toast.error(t('nestedCombo', { defaultValue: 'A bundle cannot contain another bundle.' }));
      return;
    }
    itemsById.current.set(item.id, item);
    setLines((prev) => {
      const existing = prev.find((l) => l.componentItemId === item.id);
      if (existing) {
        return prev.map((l) => (l.componentItemId === item.id
          ? { ...l, quantity: safeDecimal(l.quantity).plus(safeDecimal(addBase)).toString() }
          : l));
      }
      return [...prev, {
        _key: nextKey(),
        componentItemId: item.id,
        code: item.code,
        name: item.name,
        uom: item.unitOfMeasure,
        quantity: trimMoney(addBase),
        unitSalePrice: item.salePrice ?? null,
        saleCurrencyCode: item.saleCurrencyCode ?? null,
        onHand: item.onHand ?? '0',
      }];
    });
    focusScan();
  };

  const onScanPick = (value: string | undefined): void => {
    if (!value) return;
    setTerm('');
    setSearch({ q: '', items: [] });
    const item = itemsById.current.get(value);
    if (item) addItem(item, '1');
  };

  const resolveScan = async (code: string): Promise<void> => {
    setTerm('');
    setSearch({ q: '', items: [] });
    try {
      const { data } = await api().get<{ item: Item; itemUnitId: string | null }>('/items/resolve', { params: { barcode: code } });
      const unit = data.itemUnitId ? data.item.units.find((u) => u.id === data.itemUnitId) : undefined;
      addItem(data.item, unit ? unit.factor : '1');
    } catch {
      toast.error(t('unknownBarcode', { defaultValue: 'No product found for that barcode.' }));
    }
  };

  const usable = lines.filter((l) => safeDecimal(l.quantity).gt(0));


  /**
   * What the parts cost separately, and how many bundles the shelf can cover.
   * Only components priced in the bundle's OWN currency are added up — quoting
   * a saving across a currency we haven't converted would be a made-up number,
   * so a mixed bundle says so instead.
   */
  const summary = useMemo(() => {
    const ccy = saleCurrencyCode;
    let parts = safeDecimal('0');
    let priced = true;
    let buildable: ReturnType<typeof safeDecimal> | null = null;
    for (const l of usable) {
      const qty = safeDecimal(l.quantity);
      if (l.unitSalePrice != null && l.saleCurrencyCode === ccy && ccy) {
        parts = parts.plus(safeDecimal(l.unitSalePrice).times(qty));
      } else {
        priced = false;
      }
      const canMake = qty.gt(0) ? safeDecimal(l.onHand).div(qty).floor() : safeDecimal('0');
      const clamped = canMake.lt(0) ? safeDecimal('0') : canMake;
      buildable = buildable === null ? clamped : (clamped.lt(buildable) ? clamped : buildable);
    }
    const price = safeDecimal(normalizeNumericInput(salePrice) || '0');
    const saves = priced && price.gt(0) ? parts.minus(price) : null;
    const percent = saves && parts.gt(0) ? saves.div(parts).times(100) : null;
    return { parts, priced, saves, percent, buildable: buildable ?? safeDecimal('0') };
  }, [usable, salePrice, saleCurrencyCode]);

  const canSave = canWrite && usable.length > 0 && !saving;

  const save = async (): Promise<void> => {
    if (!canSave) return;
    setSaving(true);
    try {
      const body: ComboUpsert = { lines: comboRowsToPayload(lines) };
      await api().put(`/combos/by-item/${itemId}`, body);
      setExists(true);
      toast.success(t('saved', { defaultValue: 'Bundle saved.' }));
    } catch (err) {
      const code = (err as { response?: { data?: { cause?: { code?: string } } } }).response?.data?.cause?.code;
      const messages: Record<string, string> = {
        'combo.self_reference': t('selfReference', { defaultValue: 'A bundle cannot contain itself.' }),
        'combo.nested_combo': t('nestedCombo', { defaultValue: 'A bundle cannot contain another bundle.' }),
        'combo.has_recipe': t('hasRecipe', { defaultValue: 'This product already has a manufacturing recipe. A product is either made in the factory or bundled at the shelf, not both.' }),
        'combo.not_a_product': t('notAProduct', { defaultValue: 'Only products can be bundles.' }),
        'combo.duplicate_component': t('duplicateComponent', { defaultValue: 'The same product is listed twice — add up the quantity instead.' }),
      };
      toast.error((code && messages[code]) ?? t('saveFailed', { defaultValue: 'Could not save the bundle.' }));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (): Promise<void> => {
    const ok = await confirmDialog({
      title: t('deleteTitle', { defaultValue: 'Delete this bundle?' }),
      message: t('deleteConfirm', { defaultValue: 'The product stays; it just stops being a bundle and sells on its own again. Past sales keep their own records.' }),
      tone: 'danger',
      confirmLabel: t('delete', { defaultValue: 'Delete bundle' }),
    });
    if (!ok) return;
    try {
      await api().delete(`/combos/by-item/${itemId}`);
      setExists(false);
      setLines([]);
      toast.success(t('deleted', { defaultValue: 'Bundle deleted.' }));
    } catch {
      toast.error(t('saveFailed', { defaultValue: 'Could not save the bundle.' }));
    }
  };

  if (!loaded) return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;

  return (
    <div className="flex flex-col gap-4">
      <Card className="p-6">
        <h2 className="mb-1 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-fg-muted">
          <Package className="h-4 w-4" aria-hidden />
          {t('title', { defaultValue: 'Bundle (what the customer gets)' })}
        </h2>
        <p className="text-sm text-fg-muted">
          {t('hint', { defaultValue: 'Sell this at one price and these products come off the shelf automatically. They stay ordinary products — still sold on their own as always.' })}
        </p>
        {!itemId && (
          <p className="mt-2 text-sm text-fg-muted">
            {t('createHint', { defaultValue: 'Add what goes inside now — it is saved together with the product.' })}
          </p>
        )}

        {usable.length > 0 && (
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-border bg-bg-subtle p-3">
              <div className="text-xs text-fg-muted">{t('partsTotal', { defaultValue: 'Parts bought separately' })}</div>
              <div className="mt-1 font-mono text-lg">
                {summary.priced && saleCurrencyCode
                  ? formatMoney(summary.parts.toString(), { locale: i18n.language, currency: saleCurrencyCode })
                  : <span className="text-sm text-fg-muted">{t('partsUnknown', { defaultValue: 'Some parts have no price in this currency' })}</span>}
              </div>
            </div>
            <div className="rounded-lg border border-border bg-bg-subtle p-3">
              <div className="text-xs text-fg-muted">{t('savings', { defaultValue: 'Customer saves' })}</div>
              <div className="mt-1 font-mono text-lg">
                {summary.saves && saleCurrencyCode ? (
                  <span className={summary.saves.gt(0) ? 'text-success' : 'text-danger'}>
                    {formatMoney(summary.saves.toString(), { locale: i18n.language, currency: saleCurrencyCode })}
                    {summary.percent && <span className="ms-2 text-sm">({summary.percent.toDecimalPlaces(0).toString()}%)</span>}
                  </span>
                ) : <span className="text-sm text-fg-muted">—</span>}
              </div>
            </div>
            <div className="rounded-lg border border-border bg-bg-subtle p-3">
              <div className="text-xs text-fg-muted">{t('buildable', { defaultValue: 'Can sell right now' })}</div>
              <div className="mt-1 font-mono text-lg">{summary.buildable.toString()}</div>
            </div>
          </div>
        )}
      </Card>

      {canWrite && (
        <div
          ref={scanWrapRef}
          onInput={(e) => setTerm((e.target as HTMLInputElement).value)}
          className="flex items-center gap-2 rounded-lg border border-border bg-bg-subtle px-3 py-2"
        >
          <ScanLine className="h-5 w-5 flex-none text-fg-muted" aria-hidden />
          <div className="flex-1">
            <Combobox
              options={scanOptions}
              onChange={onScanPick}
              onEnterUnmatched={(code) => void resolveScan(code)}
              clearOnSelect
              placeholder={t('scanAdd', { defaultValue: 'Scan a barcode or search to add a product to the bundle…' })}
              ariaLabel={t('scanAdd', { defaultValue: 'Scan a barcode or search to add a product to the bundle…' })}
            />
          </div>
        </div>
      )}

      <Card className="overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-bg-muted text-fg-subtle">
            <tr>
              <th className="w-10 px-3 py-2 text-start font-medium">#</th>
              <th className="px-3 py-2 text-start font-medium">{t('component', { defaultValue: 'Product' })}</th>
              <th className="w-32 px-3 py-2 text-end font-medium">{t('onHand', { defaultValue: 'In stock' })}</th>
              <th className="w-40 px-3 py-2 text-end font-medium">{t('perCombo', { defaultValue: 'Qty per bundle' })}</th>
              {canWrite && <th className="w-12" />}
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-10 text-center text-sm text-fg-muted">
                  {t('emptyGrid', { defaultValue: 'Scan a barcode or search above to add the first product.' })}
                </td>
              </tr>
            ) : lines.map((l, idx) => (
              <tr key={l._key} className="border-t border-border">
                <td className="px-3 py-2 text-fg-muted">{idx + 1}</td>
                <td className="px-3 py-2">
                  <div className="font-medium">{l.name}</div>
                  <div className="font-mono text-xs text-fg-muted">{l.code} · {l.uom}</div>
                </td>
                <td className="px-3 py-2 text-end font-mono text-fg-muted">{trimMoney(l.onHand)}</td>
                <td className="px-3 py-2">
                  <NumberInput
                    className="text-end font-mono"
                    value={l.quantity}
                    disabled={!canWrite}
                    onValueChange={(raw) => {
                      const clean = normalizeNumericInput(raw);
                      setLines((prev) => prev.map((x) => (x._key === l._key ? { ...x, quantity: clean } : x)));
                    }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); focusScan(); } }}
                    aria-label={t('perCombo', { defaultValue: 'Qty per bundle' })}
                  />
                </td>
                {canWrite && (
                  <td className="px-3 py-2 text-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label={t('removeLine', { defaultValue: 'Remove product' })}
                      onClick={() => setLines((prev) => prev.filter((x) => x._key !== l._key))}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Before the product exists there is nothing to PUT to — the page's own
          Save button is what persists the box, so this row would be a second,
          contradictory Save. */}
      {canWrite && itemId && (
        <div className="flex items-center justify-end gap-2">
          {exists && (
            <Button type="button" variant="secondary" onClick={() => void remove()}>
              <Trash2 className="me-1.5 h-4 w-4" aria-hidden />
              {t('delete', { defaultValue: 'Delete bundle' })}
            </Button>
          )}
          <Button type="button" disabled={!canSave} loading={saving} onClick={() => void save()}>
            {t('save', { defaultValue: 'Save bundle' })}
          </Button>
        </div>
      )}
    </div>
  );
}
